import { cn } from "@/lib/utils";

interface CapacityStripProps {
    /** Total scheduled minutes assigned to this tech for the day. */
    bookedMinutes: number;
    /** Tech's daily capacity in minutes (defaults to 8h at the call site). */
    capacityMinutes: number;
    /** Productive points booked for the day (points-capacity model). */
    bookedPoints?: number;
    /** Tech's soft daily points target (e.g. 20). */
    pointsTarget?: number | null;
    /** Tech's hard daily points cap. Over this the meter goes red. */
    pointsCap?: number | null;
}

/**
 * Per-lane load meter. #587: **points are the primary weight read** — how full
 * a tech's day is now reads off the points meter, not how much of the lane the
 * cards happen to fill. The meter fills against the soft target, tints amber as
 * it approaches, and goes **red over the hard cap** (or over target when no cap
 * is set). Hours ride along as a thin secondary line. When a tech has no points
 * target we fall back to the hours bar as the primary read (unchanged).
 */
export function CapacityStrip({
    bookedMinutes,
    capacityMinutes,
    bookedPoints,
    pointsTarget,
    pointsCap,
}: CapacityStripProps) {
    const safeCapacity = Math.max(60, capacityMinutes);
    const hours = (bookedMinutes / 60).toFixed(1);
    const cap = (safeCapacity / 60).toFixed(1);

    const hasPoints = bookedPoints != null && pointsTarget != null && pointsTarget > 0;

    if (hasPoints) {
        const target = pointsTarget as number;
        const booked = bookedPoints as number;
        const hardCap = pointsCap != null && pointsCap > 0 ? pointsCap : target;
        const ratio = booked / target;
        const pct = Math.min(100, Math.max(0, ratio * 100));
        const over = booked > hardCap;
        const near = !over && ratio >= 0.85;
        const tone = over ? "bg-red-500" : near ? "bg-amber-500" : "bg-emerald-500";
        // Cap marker: only when a distinct hard cap sits within the bar's scale.
        const capPct = hardCap > target ? Math.min(100, (target / hardCap) * 100) : null;
        const ptsLabel = Math.round(booked * 10) / 10;

        return (
            <div className="text-[10px] text-foreground/80">
                <div className="flex items-center justify-between gap-1 leading-none">
                    <span className="font-semibold uppercase tracking-wide text-foreground/60">Points</span>
                    <span
                        className={cn(
                            "font-bold tabular-nums",
                            over ? "text-red-600 dark:text-red-400" : "text-foreground",
                        )}
                        title={`Productive points today vs target (${target})${
                            pointsCap ? `, hard cap ${pointsCap}` : ""
                        }. Travel pay is separate.`}
                    >
                        {ptsLabel} / {target}
                        {over && <span className="ml-1 font-semibold text-red-600 dark:text-red-400">over</span>}
                    </span>
                </div>
                <div
                    className="relative mt-1 h-2 w-full min-w-[48px] overflow-hidden rounded-full bg-muted"
                    title={`How full this tech's day is: points booked (${ptsLabel}) fill toward their daily target (${target}). Green has room, amber is close, red is over${pointsCap ? ` the hard cap (${pointsCap})` : ""}. Travel pay is separate.`}
                >
                    <div className={cn("absolute inset-y-0 left-0 rounded-full", tone)} style={{ width: `${pct}%` }} />
                    {capPct != null && (
                        <div
                            className="absolute inset-y-[-1px] w-px bg-foreground/50"
                            style={{ left: `${capPct}%` }}
                            title={`Target ${target} · cap ${hardCap}`}
                        />
                    )}
                </div>
                <div
                    className={cn(
                        "mt-1 leading-none text-foreground/55",
                        bookedMinutes > safeCapacity && "text-red-500",
                    )}
                >
                    {hours}h / {cap}h scheduled
                </div>
            </div>
        );
    }

    // Fallback: no points target — keep the original hours meter as primary.
    const ratio = bookedMinutes / safeCapacity;
    const pct = Math.min(100, Math.max(0, ratio * 100));
    const overflow = ratio > 1 ? Math.min(100, (ratio - 1) * 100) : 0;
    const tone = ratio > 1 ? "bg-red-500" : ratio > 0.75 ? "bg-amber-500" : "bg-emerald-500";

    return (
        <div className="flex items-center gap-2 text-[10px] text-foreground/80">
            <div className="relative h-1.5 w-full min-w-[48px] flex-1 overflow-hidden rounded-full bg-muted">
                <div className={cn("absolute inset-y-0 left-0 rounded-full", tone)} style={{ width: `${pct}%` }} />
                {overflow > 0 && (
                    <div
                        className="absolute inset-y-0 right-0 rounded-full bg-red-700/70"
                        style={{ width: `${overflow}%` }}
                    />
                )}
            </div>
            <span className={cn(ratio > 1 && "font-medium text-red-600 dark:text-red-400")}>
                {hours}h / {cap}h
            </span>
        </div>
    );
}
